<script setup>
import { ref } from 'vue'

defineProps({
  variation: {
    type: String,
    default: 'color', // simple, split-image, full-width, no-curve, color(default)
  },
  color: {
    type: String,
    default: 'aqua', // navy, aqua(default)
  },
  title: {
    type: String,
  },
  description: {
    type: String,
  },
  image: {
    type: String,
    default: '/images/hero-full-width.jpg', // image name + extension
  },
  imageAltText: {
    type: String,
    default: 'Hero Image',
  },
  imageWidth: {
    type: [Number, String],
    default: null,
  },
  imageHeight: {
    type: [Number, String],
    default: null,
  },
  body: {
    type: String,
  },
})

const imageError = ref(false)

function handleImageError() {
  imageError.value = true
}
</script>

<template>
  <div
    v-if="variation === 'simple'"
    class="hero hero--simple"
  >
    <div class="container-fluid">
      <div class="hero-wrap">
        <NuxtImg
          v-if="image"
          provider="fastlyAprimo"
          :src="image"
          class="hero__image"
          :alt="imageAltText"
          :width="imageWidth ?? 1162"
          fit="bounds"
          sizes="(max-width: 768px) 100vw, 1162px"
        />
        <div class="hero__body prose">
          <slot />
          <h1 v-if="title">
            {{ title }}
          </h1>
          <p>{{ description }}</p>
          <template v-if="body">
            <div v-html="body" />
          </template>
          <slot />
        </div>
      </div>
    </div>
  </div>

  <div
    v-else-if="variation === 'split-image'"
    class="hero hero--split-image"
    :class="{ 'hero--navy dark-bg': color === 'navy' }"
  >
    <div class="container-fluid">
      <div class="hero-wrap">
        <NuxtImg
          v-if="image"
          provider="fastlyAprimo"
          :src="image"
          class="hero__image"
          :alt="imageAltText"
          :width="imageWidth ?? 1162"
          fit="bounds"
          sizes="(max-width: 768px) 100vw, 1162px"
        />
        <div class="hero__body prose">
          <h1 v-if="title">
            {{ title }}
          </h1>
          <p>{{ description }}</p>
          <template v-if="body">
            <div v-html="body" />
          </template>
          <slot />
        </div>
      </div>
    </div>
  </div>

  <div
    v-else-if="variation === 'full-width'"
    class="hero hero--full-width"
    :class="{ 'hero--navy dark-bg': color === 'navy' }"
  >
    <NuxtImg
      v-if="image && !imageError"
      provider="fastlyAprimo"
      :src="image"
      class="hero__image"
      :alt="imageAltText"
      :width="imageWidth ?? 1162"
      fit="bounds"
      sizes="(max-width: 768px) 100vw, 1162px"
      @error="handleImageError"
    />
    <div class="container-fluid">
      <div class="hero__body prose">
        <h1 v-if="title">
          {{ title }}
        </h1>
        <p>{{ description }}</p>
        <template v-if="body">
          <div v-html="body" />
        </template>
        <slot />
      </div>
    </div>
  </div>

  <div
    v-else-if="variation === 'no-curve'"
    class="hero hero--no-curve"
  >
    <div class="container-fluid">
      <div class="hero__body prose">
        <slot />
        <h1 v-if="title">
          {{ title }}
        </h1>
        <p>{{ description }}</p>
      </div>
    </div>
  </div>

  <div
    v-else-if="variation === 'color'"
    class="hero hero--color"
    :class="{ 'hero--navy dark-bg': color === 'navy' }"
  >
    <div class="container-fluid">
      <div class="hero__body prose">
        <h1 v-if="title">
          {{ title }}
        </h1>
        <p>{{ description }}</p>
        <template v-if="body">
          <div v-html="body" />
        </template>
        <slot />
      </div>
    </div>
  </div>

  <div
    v-else-if="variation === 'small-image'"
    class="hero hero--small-img"
    :class="{ 'hero--navy dark-bg': color === 'navy' }"
  >
    <div class="container-fluid">
      <div class="hero__body-wrap">
        <div class="hero__body prose">
          <h1 v-if="title">
            {{ title }}
          </h1>
          <p>{{ description }}</p>
          <slot />
        </div>
        <div class="hero__body-image-wrap">
          <NuxtImg
            provider="fastlyAprimo"
            :src="image"
            class="hero__body-image rounded-xl"
            :alt="imageAltText"
            :width="imageWidth ?? 599"
            fit="bounds"
            sizes="(max-width: 1024px) 100vw, 599px"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '@/assets/css/blocks/hero-block.scss';
</style>
